import GooglePlayAndAppStoreBadgeLinks from '@app-studio-web/ui/components/GooglePlayAndAppStoreBadgeLinks'
import { useAppConfig } from '@app-studio-web/ui/contexts/AppConfigContext'
import { useTranslation } from 'react-i18next'
import { Form, Link, useNavigate } from 'react-router-dom'

const FLAG_EMOJIS = [
  '🇬🇧',
  '🇩🇪',
  '🇮🇹',
  '🇫🇷',
  '🇪🇸',
  '🇵🇹',
  '🇳🇱',
  '🇯🇵',
  '🇨🇳',
  '🇰🇷',
  '🇷🇺',
  '🇮🇩',
  '🇵🇱',
  '🇸🇪',
  '🇹🇷',
]

const SPEECH_BUBBLES = [
  { text: '¡Hola!', className: 'top-[15%] left-[5%] md:left-[10%]', delay: 0 },
  {
    text: 'Bonjour!',
    className: 'top-[45%] left-[2%] md:left-[6%]',
    delay: 0.6,
  },
  {
    text: 'Ciao!',
    className: 'bottom-[20%] left-[8%] md:left-[12%]',
    delay: 1.2,
  },
  {
    text: 'こんにちは',
    className: 'top-[20%] right-[3%] md:right-[8%]',
    delay: 0.3,
  },
  {
    text: 'Hallo!',
    className: 'top-[50%] right-[2%] md:right-[6%]',
    delay: 0.9,
  },
  {
    text: '안녕!',
    className: 'bottom-[18%] right-[6%] md:right-[11%]',
    delay: 1.5,
  },
]

export default function LandingPage() {
  const { logo } = useAppConfig()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    navigate('/onboarding')
  }

  return (
    <div className="page-bg-gradient relative flex min-h-dvh flex-col overflow-hidden">
      {SPEECH_BUBBLES.map((bubble, i) => (
        <div
          key={i}
          className={`absolute ${bubble.className} text-primary z-0 hidden rounded-2xl bg-white/60 px-4 py-2 text-sm font-semibold shadow-md backdrop-blur-sm md:block`}
          style={{
            animation: `lp-float ${3 + i * 0.4}s ease-in-out infinite`,
            animationDelay: `${bubble.delay}s`,
          }}
        >
          {bubble.text}
        </div>
      ))}

      {!('ReactNativeWebView' in window) && (
        <header className="relative z-10 mx-auto w-full max-w-5xl px-6 py-4">
          <div className="flex items-center justify-between">
            {logo}
            <div className="flex items-center gap-4">
              <Link
                className="font-headline text-custom-black text-[14px] font-bold hover:opacity-80"
                to="/account/sign-in"
                data-testid="button-login"
              >
                {t('shared.signIn')}
              </Link>
              <button
                onClick={() => navigate('/onboarding')}
                className="bg-primary cursor-pointer rounded-xl px-5 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
              >
                {t('buttons.startNow')}
              </button>
            </div>
          </div>
        </header>
      )}

      <main className="relative z-10 mx-auto flex max-w-3xl flex-1 flex-col items-center justify-center gap-8 px-8 py-12">
        <div className="text-center">
          <div className="bg-primary/10 text-primary mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold">
            <span className="bg-primary inline-block h-1.5 w-1.5 animate-pulse rounded-full" />
            {t('pages.landing.badge')}
          </div>

          <h1
            className="text-[34px] leading-[1.08] font-extrabold tracking-tight text-[#0a1628] md:text-[48px] lg:text-[56px]"
            data-testid="label-header"
          >
            {t('pages.landing.heading')}
          </h1>

          <p
            className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-[#4a5568] md:text-[20px]"
            data-testid="label-subheading"
          >
            {t('pages.landing.subheading')}
          </p>

          <Form method="post" onSubmit={onSubmit}>
            <button
              className="bg-primary shadow-primary/25 hover:shadow-primary/35 mt-8 inline-flex cursor-pointer items-center gap-2 rounded-2xl px-10 py-4 text-lg font-bold text-white uppercase shadow-lg transition-all hover:shadow-xl hover:brightness-110 active:scale-[0.98]"
              data-testid="button-continue"
              type="submit"
            >
              {t('buttons.startNow')}
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 10h12m0 0l-4-4m4 4l-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </Form>

          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="flex -space-x-2">
              {FLAG_EMOJIS.map((flag, i) => (
                <span
                  key={i}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#f0f7ff] text-sm shadow-sm"
                >
                  {flag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <GooglePlayAndAppStoreBadgeLinks />
          </div>
        </div>
      </main>

      <footer className="relative z-10 mt-auto px-6 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-3 text-base text-[#94a3b8]">
          <Link
            to="https://app-studio.ai/terms"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.terms')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="https://app-studio.ai/privacy"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.privacy')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/contact"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.contact', { defaultValue: 'Contact' })}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/cancel-subscription"
            className="transition-colors hover:text-[#475569]"
          >
            {t('footer.cancelSubscription', {
              defaultValue: 'How to Cancel Subscription',
            })}
          </Link>
        </div>
      </footer>

      <style>{`
        @keyframes lp-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  )
}
